import { useRef } from 'react';
import { AuthenticatedTemplate, useMsal } from '@azure/msal-react';
import styled from '@emotion/styled/macro';
import { useLocation } from 'react-router-dom';
import {
    Spacer,
    Button,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useIntl, FormattedMessage } from 'react-intl';

import Logo from '../utils/Logo';
import CustomNavLink from '../utils/CustomNavLink';

import MainNavigation from './MainNavigation';
import MobileNavigationDrawer from './mobileNavigation/MobileNavigationDrawer';

import { ReactComponent as HamburgerNavIcon } from '../../icons/hamburger-nav.svg';
import { ReactComponent as ArrowDropDownIcon } from '../../icons/arrow_drop_down.svg';

const AppBar = () => {
    const intl = useIntl();
    const { accounts, instance } = useMsal();
    const name = accounts.length > 0 ? accounts[0].name : '';

    const { isOpen, onOpen, onClose } = useDisclosure();
    const btnRef = useRef();

    const location = useLocation();

    const isDashboardRoute = location.pathname === '/';
    const isConnectionRoute = location.pathname.startsWith('/connection');

    return (
        <AuthenticatedTemplate>
            <Wrapper isDashboardRoute={isDashboardRoute}>
                <MobileMenuWrapper>
                    <HamburgerNavIcon ref={btnRef} onClick={onOpen} />
                </MobileMenuWrapper>

                <LogoWrapper isDashboardRoute={isDashboardRoute}>
                    <CustomNavLink to="/">
                        <Logo />
                    </CustomNavLink>
                </LogoWrapper>

                {isConnectionRoute ? <Spacer display={{ base: 'none', lg: 'block' }} /> : <MainNavigation />}

                <AccountActions>
                    <Menu>
                        <MenuButton
                            as={Button}
                            aria-label={intl.formatMessage({ id: 'common_account' })}
                            rightIcon={<ArrowDropDownIcon />}
                            variant="ghost"
                            iconSpacing={0}
                            p="0 0 0 8px"
                        >
                            <Text as="span" display="inline-block" maxW={116} isTruncated>
                                {name}
                            </Text>
                        </MenuButton>

                        <MenuList>
                            <MenuItem as={CustomNavLink} to="/connection">
                                <FormattedMessage id="connection_connection" />
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem onClick={() => instance.logoutRedirect()} textTransform="capitalize">
                                <FormattedMessage id="common_sign_out" />
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </AccountActions>

                <MobileNavigationDrawer isOpen={isOpen} onClose={onClose} userName={name} btnRef={btnRef} />
            </Wrapper>
        </AuthenticatedTemplate>
    );
};

const MobileMenuWrapper = styled.button`
    @media screen and (min-width: 900px) {
        display: none;
    }
`;

const LogoWrapper = styled('div', { shouldForwardProp: (prop) => prop !== 'isDashboardRoute' })`
    display: ${({ isDashboardRoute }) => isDashboardRoute && 'none'};
    margin-right: 30px;

    a {
        display: inline-block;
        border-radius: var(--chakra-radii-base);
    }

    a:focus {
        outline: 0;
        box-shadow: var(--chakra-shadows-outline);
    }

    @media screen and (max-width: 899px) {
        margin: 0 auto;
        display: block;
    }
`;

const Wrapper = styled('div', { shouldForwardProp: (prop) => prop !== 'isDashboardRoute' })`
    width: 100%;
    position: fixed;
    padding: ${({ isDashboardRoute }) => (isDashboardRoute ? '0 24px' : '0 16px')};
    min-height: 80px;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: var(--chakra-shadows-sm);
    background: var(--chakra-colors-white);

    @media screen and (max-width: 899px) {
        display: flex;
        justify-content: space-between;
        padding: 0 24px;
        margin-left: 0;
    }
`;

const AccountActions = styled.div`
    @media screen and (max-width: 899px) {
        display: none;
    }

    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-right: 0;

    svg {
        opacity: 0.3;
    }
`;

export default AppBar;
